import { useState, useMemo } from "react";

const PLANS = {
  start: {
    id: "start", label: "Start", price: 99.90, priceMin: 79.90, priceMax: 99.90,
    included: 50, excedente: 1.99,
  },
  pro: {
    id: "pro", label: "Pro", price: 199.90, priceMin: 199.90, priceMax: 249.90,
    included: 150, excedente: 1.49,
  },
  max: {
    id: "max", label: "Max", price: 399.90, priceMin: 399.90, priceMax: 499.90,
    included: 400, excedente: 0.99,
  },
};

const fmt = v => "R$ " + Math.round(v).toLocaleString("pt-BR");
const fmtDec = v => "R$ " + v.toFixed(2).replace(".", ",");
const fmtPct = v => v + "%";

const CY = "#00D4E8";
const CY_MID = "#0070C0";
const ND = "#0D1B2A";
const NV = "#1A3A5C";
const NM = "#3D5A80";
const NL = "#8FA4B8";
const NB = "#C9D6E3";
const NW = "#E8EEF4";
const BG = "#F0F4F8";
const W = "#FFFFFF";
const OK = "#2E7D32";
const OK_BG = "#E8F5E9";
const WN = "#E65100";
const WN_BG = "#FFF3E0";
const ER = "#CC0000";
const ER_BG = "#FFEBEE";

function Slider({ label, value, min, max, step, onChange, format, hint }) {
  const css = `
    .dz-slider { -webkit-appearance: none; appearance: none; width: 100%; height: 32px; background: transparent; cursor: pointer; outline: none; }
    .dz-slider::-webkit-slider-runnable-track { height: 4px; background: ${NB}; border-radius: 2px; }
    .dz-slider::-webkit-slider-thumb { -webkit-appearance: none; appearance: none; width: 20px; height: 20px; border-radius: 50%; background: ${CY}; border: 2px solid ${W}; box-shadow: 0 1px 4px rgba(13,27,42,0.18); margin-top: -8px; }
    .dz-slider::-moz-range-track { height: 4px; background: ${NB}; border-radius: 2px; }
    .dz-slider::-moz-range-thumb { width: 20px; height: 20px; border-radius: 50%; background: ${CY}; border: 2px solid ${W}; box-shadow: 0 1px 4px rgba(13,27,42,0.18); }
  `;
  return (
    <div style={{ marginBottom: 22 }}>
      <style>{css}</style>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 6, gap: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: NV }}>{label}</div>
          {hint && <div style={{ fontSize: 12, color: NL, marginTop: 3, lineHeight: 1.45 }}>{hint}</div>}
        </div>
        <div style={{ fontSize: 20, fontWeight: 800, color: CY_MID, whiteSpace: "nowrap" }}>{format(value)}</div>
      </div>
      <input type="range" className="dz-slider" min={min} max={max} step={step || 1} value={value} onChange={e => onChange(+e.target.value)} />
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 2 }}>
        <span style={{ fontSize: 11, color: NL }}>{format(min)}</span>
        <span style={{ fontSize: 11, color: NL }}>{format(max)}</span>
      </div>
    </div>
  );
}

function KPI({ label, value, sub, color, bg, borderColor }) {
  const useTint = !bg;
  return (
    <div style={{
      background: bg || "rgba(0,212,232,0.05)",
      border: "1px solid " + (borderColor || (useTint ? "rgba(0,212,232,0.14)" : "transparent")),
      borderRadius: 12, padding: "14px 16px", flex: 1, minWidth: 0
    }}>
      <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: NL, marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color: color || CY_MID, lineHeight: 1.1 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: NM, marginTop: 4, lineHeight: 1.4 }}>{sub}</div>}
    </div>
  );
}

function Section({ children, style }) {
  return <div style={{ background: W, border: "1px solid " + NB, borderRadius: 12, padding: 20, ...style }}>{children}</div>;
}

function StepHeader({ num, title, sub }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 18 }}>
      <div style={{ width: 28, height: 28, borderRadius: "50%", background: CY, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 800, color: ND }}>{num}</div>
      <div>
        <div style={{ fontSize: 14, fontWeight: 800, color: ND }}>{title}</div>
        {sub && <div style={{ fontSize: 12, color: NM, marginTop: 1 }}>{sub}</div>}
      </div>
    </div>
  );
}

function suggestPlan(agendas, slots) {
  if (agendas >= 4) return { enterprise: true, label: "Sob medida" };
  if (agendas >= 2) return PLANS.max;
  if (slots > 400) return PLANS.max;
  if (slots > 150) return PLANS.pro;
  return PLANS.start;
}

export default function App() {
  const [userType, setUserType] = useState("dentista");
  const [numDentistas, setNumDentistas] = useState(3);
  const [dias, setDias] = useState(22);
  const [cpd, setCpd] = useState(8);
  const [ns, setNs] = useState(18);
  const [ticket, setTicket] = useState(300);
  const [cf, setCf] = useState(22000);
  const [imp, setImp] = useState(10);

  function pickDentista() {
    setUserType("dentista");
    setDias(22); setCpd(8); setNs(18); setTicket(300); setCf(22000);
  }
  function pickClinica() {
    setUserType("clinica");
    setNumDentistas(3);
    setDias(22); setCpd(24); setNs(18); setTicket(300); setCf(44000);
  }

  const agendas = userType === "clinica" ? numDentistas : 1;

  const r = useMemo(() => {
    const slots = dias * cpd;
    const nsSlots = Math.round(slots * ns / 100);
    const feitas = slots - nsSlots;
    const ocup = slots > 0 ? Math.round((feitas / slots) * 100) : 0;
    const recAtual = feitas * ticket;
    const chora = cf / Math.max(1, dias * 8);
    const lucCess = nsSlots * ticket;
    const cfNs = nsSlots * chora;
    const custoNs = lucCess + cfNs;

    const consultasRecuperadas = Math.round(feitas * imp / 100);
    const recRecuperada = consultasRecuperadas * ticket;
    const custoEvitado = consultasRecuperadas * chora;

    const reativados = Math.round(feitas * 0.05);
    const recReativacao = reativados * ticket;

    const ganhoTotal = recRecuperada + recReativacao;

    const plan = suggestPlan(agendas, slots);
    const price = plan.enterprise ? 0 : plan.price;
    const roi = plan.enterprise ? null : ganhoTotal - price;
    const mult = plan.enterprise || price === 0 ? null : ganhoTotal / price;
    const minConsultas = plan.enterprise ? null : Math.ceil(price / Math.max(1, ticket));
    const payback = plan.enterprise || ganhoTotal <= 0 ? null : Math.max(1, Math.ceil(price / (ganhoTotal / 30)));
    const roiAnual = roi !== null ? roi * 12 : null;

    const cpdPorDentista = userType === "clinica" && numDentistas > 0 ? cpd / numDentistas : null;
    const excedenteQtd = plan.enterprise ? 0 : Math.max(0, slots - plan.included);
    const excedenteCusto = plan.enterprise ? 0 : excedenteQtd * plan.excedente;

    return {
      slots, nsSlots, feitas, ocup, recAtual, chora, lucCess, cfNs, custoNs,
      consultasRecuperadas, recRecuperada, custoEvitado, reativados, recReativacao,
      ganhoTotal, plan, price, roi, mult, minConsultas, payback, roiAnual,
      cpdPorDentista, excedenteQtd, excedenteCusto,
    };
  }, [dias, cpd, ns, ticket, cf, imp, agendas, userType, numDentistas]);

  const v = r.plan.enterprise ? { t: "Perfil de multi-unidade", s: "Vamos montar uma proposta sob medida.", c: CY_MID }
    : r.roi > 3000 ? { t: "Retorno excepcional", s: "Dentzi se paga com folga.", c: OK }
    : r.roi > 1000 ? { t: "Retorno sólido", s: "Impacto claro na receita mensal.", c: OK }
    : r.roi > 0 ? { t: "Retorno positivo", s: "Funciona — dá pra ajustar as premissas pra validar.", c: OK }
    : r.roi > -100 ? { t: "No limite — revisar premissas", s: "Ticket baixo ou agenda pequena.", c: WN }
    : { t: "Fora do perfil ideal hoje", s: "Não fecha a conta com este plano.", c: ER };

  const mediaCSS = `
    @media (max-width: 900px) {
      .dz-grid { grid-template-columns: 1fr !important; }
      .dz-header { padding: 14px 16px !important; flex-wrap: wrap; gap: 10px !important; }
      .dz-container { padding: 18px 14px !important; }
      .dz-usertype { flex-direction: column !important; }
      .dz-kpi-row { flex-wrap: wrap !important; }
      .dz-kpi-row > * { min-width: 46% !important; }
      .dz-cta-row { flex-direction: column !important; }
      .dz-cta-row > a { width: 100% !important; }
      .dz-roi-big { font-size: 34px !important; }
      .dz-plan-price { font-size: 24px !important; }
    }
    @media (max-width: 480px) {
      .dz-hero-claim { font-size: 28px !important; }
      .dz-kpi-row > * { min-width: 100% !important; }
    }
  `;

  return (
    <div style={{ fontFamily: "'Plus Jakarta Sans','Segoe UI',sans-serif", background: BG, minHeight: "100vh", color: ND }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;600;800&display=swap');*{box-sizing:border-box}body{margin:0}${mediaCSS}`}</style>

      <div className="dz-header" style={{ borderBottom: "1px solid " + NB, padding: "16px 28px", display: "flex", alignItems: "center", gap: 20, background: W }}>
        <span style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-1px" }}>
          <span style={{ color: ND }}>dent</span>
          <span style={{ color: CY }}>zi</span>
        </span>
        <div style={{ width: 1, height: 18, background: NB }} />
        <span style={{ fontSize: 11, fontWeight: 600, color: NM, letterSpacing: "0.1em", textTransform: "uppercase" }}>Simulador de Retorno</span>
        <div style={{ marginLeft: "auto", fontSize: 12, color: NL }}>Quanto você recupera com a agenda cheia?</div>
      </div>

      <div className="dz-container" style={{ maxWidth: 1080, margin: "0 auto", padding: "28px 22px" }}>

        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: NM, marginBottom: 10 }}>Comece por aqui</div>
          <div className="dz-usertype" style={{ display: "flex", gap: 10 }}>
            {[
              { id: "dentista", title: "Sou Dentista", sub: "Atendo sozinho, 1 agenda", onClick: pickDentista },
              { id: "clinica", title: "Sou Proprietário de Clínica", sub: "2 ou mais dentistas na equipe", onClick: pickClinica },
            ].map(o => (
              <button key={o.id} onClick={o.onClick}
                style={{
                  flex: 1, textAlign: "left", padding: "14px 18px", borderRadius: 10,
                  border: "1.5px solid " + (userType === o.id ? CY : NB),
                  background: userType === o.id ? "rgba(0,212,232,0.06)" : W,
                  cursor: "pointer", fontFamily: "inherit",
                }}>
                <div style={{ fontSize: 14, fontWeight: 800, color: userType === o.id ? CY_MID : ND }}>{o.title}</div>
                <div style={{ fontSize: 12, color: NM, marginTop: 2 }}>{o.sub}</div>
              </button>
            ))}
          </div>
          {userType === "clinica" && (
            <div style={{ marginTop: 14, background: W, border: "1px solid " + NB, borderRadius: 10, padding: "16px 18px" }}>
              <Slider label="Quantos dentistas atendem na clínica?" value={numDentistas} min={2} max={15} onChange={setNumDentistas} format={v => v + (v === 1 ? " dentista" : " dentistas")} />
            </div>
          )}
        </div>

        <div style={{ background: W, border: "1.5px solid " + CY + "55", borderRadius: 14, padding: 22, marginBottom: 22 }}>
          {r.plan.enterprise ? (
            <div>
              <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: NL, marginBottom: 6 }}>Plano recomendado</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: ND, marginBottom: 6 }}>Sob medida — Fale conosco</div>
              <div style={{ fontSize: 13, color: NM, lineHeight: 1.55, maxWidth: 640 }}>
                Clínicas com 4+ agendas têm proposta dedicada. Entre em contato e montamos o plano certo para o seu volume.
              </div>
            </div>
          ) : (
            <div>
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
                <div style={{ minWidth: 0, flex: "1 1 220px" }}>
                  <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: NL, marginBottom: 6 }}>Plano recomendado</div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: ND }}>Dentzi {r.plan.label}</div>
                  <div style={{ fontSize: 13, color: NM, marginTop: 2 }}>Faixa em teste: {fmtDec(r.plan.priceMin)} a {fmtDec(r.plan.priceMax)} / mês</div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div className="dz-plan-price" style={{ fontSize: 28, fontWeight: 800, color: CY_MID, lineHeight: 1 }}>
                    {fmtDec(r.plan.price)}<span style={{ fontSize: 13, color: NM, fontWeight: 600 }}> / mês</span>
                  </div>
                  <div style={{ fontSize: 11, color: NM, marginTop: 6 }}>
                    <span style={{ textDecoration: "line-through", color: NL }}>Configuração R$ 1.500</span>{" "}
                    <span style={{ background: OK_BG, color: OK, padding: "2px 8px", borderRadius: 4, fontWeight: 700, fontSize: 10, letterSpacing: "0.05em" }}>GRÁTIS</span>
                  </div>
                </div>
              </div>
              <div style={{ display: "flex", gap: 10, marginTop: 16, flexWrap: "wrap" }}>
                <div style={{ flex: "1 1 180px", background: BG, borderRadius: 8, padding: "10px 14px" }}>
                  <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: NL }}>Agendamentos inclusos</div>
                  <div style={{ fontSize: 16, fontWeight: 800, color: ND, marginTop: 3 }}>{r.plan.included} / mês</div>
                </div>
                <div style={{ flex: "1 1 180px", background: BG, borderRadius: 8, padding: "10px 14px" }}>
                  <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: NL }}>Agendamento adicional</div>
                  <div style={{ fontSize: 16, fontWeight: 800, color: ND, marginTop: 3 }}>{fmtDec(r.plan.excedente)} cada</div>
                </div>
              </div>
              {r.excedenteQtd > 0 && (
                <div style={{ marginTop: 12, background: WN_BG, border: "1px solid " + WN + "33", borderRadius: 8, padding: "10px 14px", fontSize: 12, color: WN }}>
                  Sua agenda tem ~{r.slots} consultas/mês — acima dos {r.plan.included} inclusos do {r.plan.label}. Excedente estimado: {fmtDec(r.excedenteCusto)}/mês.
                </div>
              )}
            </div>
          )}
        </div>

        <div className="dz-grid" style={{ display: "grid", gridTemplateColumns: "420px 1fr", gap: 20, alignItems: "start" }}>

          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

            <Section>
              <StepHeader num={1} title="Como é a sua agenda hoje?" sub="Preencha o que você já sabe de cabeça" />
              <Slider label="Dias que você atende por mês" value={dias} min={4} max={26} onChange={setDias} format={v => v + " dias"} hint="Apenas os dias em que há atendimento" />
              <Slider label="Consultas disponíveis por dia" value={cpd} min={2} max={60} onChange={setCpd} format={v => v + " consultas"}
                hint={userType === "clinica" && r.cpdPorDentista ? `Total de horários abertos na clínica — ≈ ${r.cpdPorDentista.toFixed(1)} consultas/dia por dentista` : "Quantos horários você abre — mesmo os não preenchidos"} />
              <Slider label="Pacientes que faltam sem avisar" value={ns} min={3} max={45} onChange={setNs} format={fmtPct} hint="Média nacional em odontologia: 15% a 20%" />
              <div style={{ background: "rgba(0,212,232,0.08)", border: "1px solid rgba(0,212,232,0.22)", borderRadius: 8, padding: "10px 12px", fontSize: 12, color: NV }}>
                Consultas realizadas por mês: <strong style={{ color: CY_MID }}>{r.feitas}</strong> de {r.slots} disponíveis ({r.ocup}% de ocupação)
              </div>
            </Section>

            <Section>
              <StepHeader num={2} title="Seus números financeiros" sub="Veja a receita estimada ao lado e ajuste se precisar" />
              <div style={{ background: BG, borderRadius: 8, padding: "12px 14px", marginBottom: 18 }}>
                <div style={{ fontSize: 11, color: NM }}>Receita mensal estimada:</div>
                <div style={{ fontSize: 22, fontWeight: 800, color: CY_MID }}>{fmt(r.recAtual)}</div>
                <div style={{ fontSize: 11, color: NL, marginTop: 2 }}>{r.feitas} consultas × {fmt(ticket)}</div>
              </div>
              <Slider label="Valor médio por consulta (ticket)" value={ticket} min={50} max={1500} step={10} onChange={setTicket} format={fmt} hint="Valor líquido recebido — já descontado repasse de convênio" />
              <Slider label="Seus custos fixos mensais" value={cf} min={1000} max={250000} step={500} onChange={setCf} format={fmt} hint="Aluguel + pessoal + pró-labore. Sem materiais e impostos." />
            </Section>

            <Section style={{ border: "1.5px solid " + CY + "55" }}>
              <StepHeader num={3} title="O que você espera da Dentzi" sub="Nossa referência de mercado: +10% sobre consultas realizadas" />
              <Slider label="Aumento de consultas realizadas" value={imp} min={3} max={35} onChange={setImp} format={fmtPct} hint="Via confirmações automáticas, lembretes e reagendamento imediato de faltas" />
              <div style={{ background: OK_BG, border: "1px solid " + OK + "33", borderRadius: 8, padding: "10px 12px", fontSize: 12, color: OK }}>
                Com +{imp}% sobre as {r.feitas} consultas realizadas, {r.consultasRecuperadas === 1 ? "é 1 consulta a mais" : <><strong>são {r.consultasRecuperadas} consultas a mais</strong></>} por mês.
              </div>
            </Section>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

            <Section>
              <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: NM, marginBottom: 14 }}>Diagnóstico atual — sem Dentzi</div>
              <div className="dz-kpi-row" style={{ display: "flex", gap: 10, marginBottom: 10 }}>
                <KPI label="Consultas/mês" value={r.slots} sub="capacidade total" color={ND} bg={BG} />
                <KPI label="Faltas/mês" value={r.nsSlots} sub={ns + "% da agenda"} color={WN} bg={WN_BG} borderColor={WN + "33"} />
              </div>
              <div className="dz-kpi-row" style={{ display: "flex", gap: 10, marginBottom: 16 }}>
                <KPI label="Receita realizada" value={fmt(r.recAtual)} sub={r.feitas + " consultas"} color={ND} bg={BG} />
                <KPI label="Receita perdida" value={fmt(r.lucCess)} sub="lucro cessante direto" color={ER} bg={ER_BG} borderColor={ER + "33"} />
              </div>
              <div style={{ background: BG, borderRadius: 10, padding: 14 }}>
                <div style={{ fontSize: 10, fontWeight: 600, color: WN, marginBottom: 10, letterSpacing: "0.08em", textTransform: "uppercase" }}>Custo real de cada falta</div>
                <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                  {[
                    { label: "Receita perdida", value: fmt(ticket), sub: "lucro cessante", c: ER },
                    null,
                    { label: "Custo fixo da hora", value: fmt(r.chora), sub: "já pago, desperdiçado", c: WN },
                    null,
                    { label: "Custo real/falta", value: fmt(ticket + r.chora), sub: "por consulta perdida", c: ER, hi: true },
                  ].map((x, i) => x === null ? (
                    <span key={i} style={{ color: NL, fontWeight: 800, fontSize: 16 }}>+</span>
                  ) : (
                    <div key={i} style={{ flex: "1 1 120px", textAlign: "center", padding: "10px 6px", background: x.hi ? ER_BG : W, borderRadius: 8, border: "1px solid " + (x.hi ? ER + "33" : NB) }}>
                      <div style={{ fontSize: 10, color: NL, marginBottom: 3 }}>{x.label}</div>
                      <div style={{ fontSize: 15, fontWeight: 800, color: x.c }}>{x.value}</div>
                      <div style={{ fontSize: 10, color: NL }}>{x.sub}</div>
                    </div>
                  ))}
                </div>
                <div style={{ marginTop: 10, textAlign: "center", padding: "10px", background: ER_BG, borderRadius: 8 }}>
                  <span style={{ fontSize: 12, color: NM }}>Total perdido em faltas por mês: </span>
                  <span style={{ fontSize: 16, fontWeight: 800, color: ER }}>{fmt(r.custoNs)}</span>
                </div>
              </div>
            </Section>

            <Section style={{ border: "1.5px solid " + CY + "55" }}>
              <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: NM, marginBottom: 14 }}>Com Dentzi — +{imp}% de ocupação</div>
              <div className="dz-kpi-row" style={{ display: "flex", gap: 10, marginBottom: 10 }}>
                <KPI label="Consultas recuperadas" value={"+" + r.consultasRecuperadas} sub="por mês, via automação" color={CY_MID} />
                <KPI label="Receita recuperada" value={fmt(r.recRecuperada)} sub={r.consultasRecuperadas + " × " + fmt(ticket)} color={CY_MID} />
              </div>
              <div className="dz-kpi-row" style={{ display: "flex", gap: 10, marginBottom: 16 }}>
                <KPI label="Custo fixo evitado" value={fmt(r.custoEvitado)} sub="hora clínica que volta a produzir (informativo)" color={NM} bg={BG} />
                <KPI label="Convites de retorno" value={"+" + r.reativados} sub={"≈ " + fmt(r.recReativacao) + " adicional"} color={CY_MID} />
              </div>
              {r.plan.enterprise ? (
                <div style={{ background: BG, border: "1.5px solid " + CY + "33", borderRadius: 12, padding: 18, textAlign: "center" }}>
                  <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: NM, marginBottom: 6 }}>Ganho bruto potencial / mês</div>
                  <div className="dz-roi-big" style={{ fontSize: 40, fontWeight: 800, color: CY_MID }}>{fmt(r.ganhoTotal)}</div>
                  <div style={{ fontSize: 12, color: NM, marginTop: 8 }}>Plano sob medida — fale com a gente para ver o ROI líquido.</div>
                </div>
              ) : (
                <div style={{ background: r.roi > 0 ? OK_BG : ER_BG, border: "1.5px solid " + (r.roi > 0 ? OK : ER) + "44", borderRadius: 12, padding: 18, textAlign: "center" }}>
                  <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: NM, marginBottom: 6 }}>Retorno líquido por mês</div>
                  <div className="dz-roi-big" style={{ fontSize: 42, fontWeight: 800, color: r.roi > 0 ? OK : ER, lineHeight: 1 }}>{r.roi >= 0 ? "+" : ""}{fmt(r.roi)}</div>
                  <div style={{ fontSize: 12, color: NM, marginTop: 10, lineHeight: 1.7 }}>
                    {fmt(r.ganhoTotal)} ganho total − {fmtDec(r.price)} Dentzi {r.plan.label}/mês<br />
                    <strong style={{ color: ND }}>{fmt(r.roiAnual)}/ano</strong> · <strong style={{ color: ND }}>{r.mult.toFixed(1)}× o investimento</strong>
                  </div>
                </div>
              )}
            </Section>

            <div style={{ background: W, border: "1.5px solid " + v.c + "44", borderRadius: 12, padding: 18 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                <div style={{ width: 10, height: 10, borderRadius: "50%", background: v.c }} />
                <div>
                  <div style={{ fontSize: 15, fontWeight: 800, color: v.c }}>{v.t}</div>
                  <div style={{ fontSize: 12, color: NM }}>{v.s}</div>
                </div>
              </div>
              {!r.plan.enterprise && (
                <div style={{ background: BG, borderRadius: 8, padding: "12px 14px", fontSize: 13, color: NV, lineHeight: 1.8 }}>
                  <div>→ {r.minConsultas === 1 ? "Basta" : "Bastam"} <strong style={{ color: CY_MID }}>{r.minConsultas} consulta{r.minConsultas !== 1 ? "s" : ""} a mais/mês</strong> para pagar a Dentzi {r.plan.label}</div>
                  <div>→ Isso é <strong style={{ color: CY_MID }}>{((r.minConsultas / Math.max(1, r.slots)) * 100).toFixed(1)}% da sua agenda</strong> de {r.slots} horários</div>
                  {r.payback && <div>→ Dentzi se paga {r.payback === 1 ? "no primeiro" : "nos primeiros"} <strong style={{ color: CY_MID }}>{r.payback} dia{r.payback !== 1 ? "s" : ""} do mês</strong></div>}
                  <div style={{ marginTop: 8, paddingTop: 8, borderTop: "1px solid " + NB, color: NM, fontSize: 12 }}>
                    Você perde <strong style={{ color: WN }}>{fmt(r.custoNs)}/mês</strong> em faltas. A Dentzi {r.plan.label} custa <strong style={{ color: WN }}>{r.custoNs > 0 ? ((r.price / r.custoNs) * 100).toFixed(0) : 0}%</strong> disso.
                  </div>
                </div>
              )}
              {!r.plan.enterprise && r.roi > 0 && (
                <div style={{ marginTop: 12, background: OK_BG, borderRadius: 8, padding: "10px 14px", fontSize: 13, color: OK, fontWeight: 700, textAlign: "center" }}>
                  ✓ Faz sentido para o seu perfil
                </div>
              )}
            </div>
          </div>
        </div>

        <div style={{ background: "linear-gradient(135deg, " + ND + " 0%, " + NV + " 100%)", borderRadius: 16, padding: "32px 28px", marginTop: 28, color: W }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: CY, marginBottom: 10 }}>Dentzi Founders</div>
          <div style={{ fontSize: 24, fontWeight: 800, marginBottom: 6, color: W }}>Programa Dentzi Founders</div>
          <div style={{ fontSize: 15, color: NW, marginBottom: 10 }}>Estamos em busca das primeiras Clínicas e Dentistas Fundadores.</div>
          <div style={{ fontSize: 13, color: NL, lineHeight: 1.65, maxWidth: 720, marginBottom: 22 }}>
            As primeiras 10 clínicas terão condições exclusivas e acesso direto aos fundadores para moldar o produto juntos. Construa a Dentzi com a gente.
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12, marginBottom: 18 }}>
            {[
              "Acesso direto aos fundadores",
              "Voz no roadmap do produto",
              "Condições exclusivas permanentes",
            ].map(t => (
              <div key={t} style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(0,212,232,0.25)", borderRadius: 10, padding: "16px 18px", fontSize: 14, fontWeight: 600, color: W }}>{t}</div>
            ))}
          </div>
          <div style={{ fontSize: 12, color: NL }}>Vagas limitadas · Configuração grátis para Dentistas Fundadores</div>
        </div>

        <div style={{ marginTop: 32, textAlign: "center" }}>
          <div style={{ fontSize: 22, fontWeight: 800, color: ND, marginBottom: 6 }}>Quer saber como funciona na prática?</div>
          <div style={{ fontSize: 14, color: NM, marginBottom: 18 }}>Fale comigo diretamente. Sem compromisso — você fala com o fundador.</div>
          <div className="dz-cta-row" style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
            <a href="https://wa.me/5511944823322?text=Olá%20Érico,%20vi%20o%20simulador%20da%20Dentzi%20e%20gostaria%20de%20saber%20mais." target="_blank" rel="noreferrer"
              style={{ background: "#25D366", color: W, fontWeight: 700, padding: "12px 24px", borderRadius: 24, textDecoration: "none", fontSize: 14 }}>WhatsApp</a>
            <a href="https://calendly.com/ericoa-melo/30min" target="_blank" rel="noreferrer"
              style={{ background: CY_MID, color: W, fontWeight: 700, padding: "12px 24px", borderRadius: 24, textDecoration: "none", fontSize: 14 }}>Agendar uma conversa rápida</a>
            <a href="mailto:contato@dentzi.ai?subject=Dentzi%20-%20Interesse%20no%20simulador"
              style={{ background: W, color: ND, fontWeight: 700, padding: "12px 24px", borderRadius: 24, textDecoration: "none", fontSize: 14, border: "1.5px solid " + NB }}>Email</a>
          </div>
          <div style={{ fontSize: 12, color: NM, marginTop: 14 }}>Érico Melo · Fundador, Dentzi · (11) 94482-3322</div>
        </div>

        <div style={{ fontSize: 11, color: NL, textAlign: "center", paddingTop: 28, paddingBottom: 16, lineHeight: 1.75, maxWidth: 820, margin: "0 auto" }}>
          Custo real da falta = lucro cessante + custo fixo da hora parada · Premissa de melhoria sobre consultas realizadas · Convites de retorno: 5% das consultas realizadas · Configuração não incluída no cálculo mensal · Valores em fase de validação. O preço final será comunicado antes da cobrança.
        </div>
      </div>
    </div>
  );
}
